import React, { memo } from 'react';
import { styled } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { isFedRamp } from '@atlassian/atl-context';
import { ff } from '@atlassian/jira-feature-flagging-using-meta';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { FeatureSection } from '../../../../common/ui/feature-section/index.tsx';
import { useFeatureVisibility } from '../../../../controllers/feature-visibility-provider/index.tsx';
import CsatFeatures from './csat-features';
import CsmFeatures from './csm-features';
import DevOpsFeatures from './dev-ops-features';
import EmailFeatures from './email-features';
import messages from './messages';
import PracticesFeatures from './practices-features';
import ViewsFeatures from './views-features';

const renderGroupTitle = (message: string) => {
	const headingLevel = fg('jsmca11y-564-update-section-headings') ? 'h2' : 'h4';
	return (
		<Heading size="xsmall" as={headingLevel}>
			<GroupTitle>{message}</GroupTitle>
		</Heading>
	);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ServiceProjectSection = memo<Record<any, any>>(() => {
	const { formatMessage } = useIntl();
	const { data: projectContextData } = useProjectContext();
	const isSimplifiedProject = Boolean(projectContextData?.simplified);

	const { hasViews, hasChannels, hasEssentials, hasPractices, hasCustomerService, hasOperations } =
		useFeatureVisibility();

	return (
		<FeatureSection>
			<>
				{ff('jsm-feature-labs-views-extraction_3odmp') && (!fg('jcs_master_flag') || hasViews) && (
					<>
						{renderGroupTitle(formatMessage(messages.viewsTitle))}
						<ViewsFeatures />
					</>
				)}
				{fg('jsm_simplified_project_settings_navigation') && (
					<>
						{(!fg('jcs_master_flag') || hasChannels) && (
							<>
								{renderGroupTitle(formatMessage(messages.channelsTitle))}
								<EmailFeatures />
							</>
						)}
						{(!fg('jcs_master_flag') || hasEssentials) && (
							<>
								{renderGroupTitle(formatMessage(messages.essentialsTitle))}
								<CsatFeatures />
							</>
						)}
					</>
				)}
				{!isSimplifiedProject && (!fg('jcs_master_flag') || hasPractices) && (
					<>
						{renderGroupTitle(formatMessage(messages.workTitle))}
						<PracticesFeatures />
					</>
				)}

				{!isSimplifiedProject && !isFedRamp() && (!fg('jcs_master_flag') || hasCustomerService) && (
					<>
						{renderGroupTitle(formatMessage(messages.customerServiceTitle))}
						<CsmFeatures />
					</>
				)}

				{(!fg('jcs_master_flag') || hasOperations) && (
					<>
						{renderGroupTitle(formatMessage(messages.operationsTitle))}
						<DevOpsFeatures />
					</>
				)}
			</>
		</FeatureSection>
	);
});

export default ServiceProjectSection;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const GroupTitle = styled.span({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N90),
	marginBottom: token('space.negative.100', '-8px'),
});
